<template>
  <div class="lv-summary">
    <div class="lv-summary-head">
      <employeeAvatar :employee="employee" :size="40" />
      <div class="main">
        <div class="name">{{ name }}</div>
        <div class="sub">{{ typeName }}<template v-if="request.createdAt"> · requested {{ $filters.getShortDate(request.createdAt) }}</template></div>
      </div>
      <span v-if="request.status" :class="['status-pill', status.pill]">{{ status.label }}</span>
    </div>

    <div class="lv-facts">
      <div class="lv-fact">
        <span class="l">Dates</span>
        <span class="v">{{ range }}</span>
      </div>
      <div class="lv-fact">
        <span class="l">Length</span>
        <span class="v">{{ length }}</span>
      </div>
      <div v-if="balanceShown" class="lv-fact">
        <span class="l">{{ afterApproval ? 'Balance after approval' : 'Balance now' }}</span>
        <span :class="['v', { warn: balanceValue < 0 }]">{{ formatDays(balanceValue) }}</span>
      </div>
    </div>

    <p v-if="request.reason" class="lv-reason"><i class="pi pi-comment"></i><span>{{ request.reason }}</span></p>
  </div>
</template>

<script>
import employeeAvatar from '@/components/dashboard/Employee_Avatar.vue';
import { fullName, leaveDaysLabel, leaveStatusMeta } from '@/utils/employee';
import { formatDays, requestBalance, requestEnd, requestStart } from '@/utils/leave';

/** Who, what and when for a leave request, with the balance it affects. */
export default {
  name: 'LeaveRequestSummary',
  components: { employeeAvatar },
  props: {
    request: { type: Object, required: true },
    /** Leave type with its policy, when the caller has it (tells whether a balance is tracked). */
    leaveType: { type: Object, default: null },
    /** Show the balance as it will be once this request is approved. */
    afterApproval: { type: Boolean, default: false },
  },
  computed: {
    employee() {
      return this.request.employee ?? null;
    },
    name() {
      return fullName(this.employee) || 'Employee';
    },
    typeName() {
      // the admin dashboard sends the leave type as a plain name
      if (typeof this.request.leaveType === 'string') return this.request.leaveType;
      return this.request.leaveType?.name ?? this.leaveType?.name ?? 'Leave';
    },
    status() {
      return leaveStatusMeta(this.request.status);
    },
    range() {
      const start = requestStart(this.request);
      const end = requestEnd(this.request);
      if (!start) return '-';
      const first = this.$filters.getShortDate(start);
      return end && end !== start ? `${first} to ${this.$filters.getShortDate(end)}` : first;
    },
    length() {
      return leaveDaysLabel(this.request);
    },
    balance() {
      return requestBalance(this.request);
    },
    tracked() {
      const flag = this.leaveType?.leavePolicy?.balanceTracked ?? this.request.leaveType?.leavePolicy?.balanceTracked;
      if (typeof flag === 'boolean') return flag;
      return !!this.balance && Number(this.balance.totalAccrued ?? 0) + Number(this.balance.totalAdjusted ?? 0) > 0;
    },
    balanceShown() {
      return !!this.balance && this.tracked;
    },
    balanceValue() {
      const available = Number(this.balance?.available ?? 0);
      return this.afterApproval ? Math.round((available - Number(this.request.days ?? 0)) * 100) / 100 : available;
    },
  },
  methods: {
    formatDays,
  },
};
</script>
